import { useMemo } from 'react';
import {
    InsuranceCareTeamContactDetails,
    InsuranceCareTeamMemberDetails,
} from '../../../../primary-record-types/apiTypes/medicalProfileService';
import { getIntl } from '../../../../utils/intl';
import {
    ContactDetailField,
    nameOrCompanyRequiredCustomValidators,
    useGetRenderContactDetailsFormFields,
} from '../../ContactDetailsForm';
import { InsuranceFormValue } from '../types';
import { defaultInsuranceValues } from '../utils';
import CareTeamTypeSelect from '../CareTeamTypeSelect';
import { CenteredFormRow, SplitFormRow } from '../../../v2BaseComponents/Form';
import { Field } from 'formik';
import { FormikInputs } from '../../../v2BaseComponents/Inputs';
import { AVATAR_UPLOAD_QUALITY, CARD_ASPECT_RATIO, CARD_UPLOAD_QUALITY } from '../../../../utils/constants';
import React from 'react';
import { required } from '../../../../utils/validators';
import { SelectOption } from '../../../v2BaseComponents/Inputs/Select/types';
import { FormStack } from '../../../v2BaseComponents/Stack';
import {
    CareTeamMemberSource,
    getCareTeamMemberAvatarUrl,
    getCareTeamMemberInsuranceCardAvatarUrl,
} from '../../../../utils/careTeamUtils';

const intl = getIntl('InsuranceFields', {
    submit: 'Submit',
    insuranceCardFront: 'Insurance Card Front',
    insuranceCardBack: 'Insurance Card Back',
    insuranceProviderName: 'Insurance Provider Name',
    primaryCarrierFirstName: 'Primary Carrier First Name',
    primaryCarrierLastName: 'Primary Carrier Last Name',
    insuranceRole: 'Insurance Role',
    groupNumber: 'Group Number',
    planNumber: 'Plan Number',
    memberId: 'Member ID',
    expirationDate: 'Expiration',
    isPreferred: 'Is Preferred',
    isActive: 'Is Active',
    photo: 'Photo',
    insuranceRolePlaceholder: 'Select Insurance Role',
});

const contactDetailsFields: ContactDetailField[] = [
    'description',
    'phoneNumber',
    'faxNumber',
    'email',
    'url',
    'role',
    'address1',
    'address2',
    'city',
    'state',
    'zipCode',
];

const insuranceRoleSelectOptions: SelectOption[] = ['Health', 'Supplemental', 'Dental', 'Vision'].map((_) => ({
    label: _,
    value: _,
}));

export const useInsuranceContactDetailsFields = (isDisabled?: boolean) =>
    useGetRenderContactDetailsFormFields({
        isDisabled: isDisabled,
        fields: contactDetailsFields,
        fieldNamePrefix: 'contactDetails',
        customValidators: nameOrCompanyRequiredCustomValidators,
    });

export const useInsuranceInitialValues = (
    initialValues?: InsuranceCareTeamMemberDetails<InsuranceCareTeamContactDetails>,
) =>
    useMemo<InsuranceFormValue>(() => {
        if (!initialValues) return defaultInsuranceValues;

        return {
            ...initialValues,
            avatar: null,
            insuranceCardFront: null,
            insuranceCardBack: null,
        };
    }, [initialValues]);

export type ProviderFieldsProps = {
    careTeamMemberId?: string;
    isDisabled?: boolean;
    source: CareTeamMemberSource;
    values: InsuranceFormValue;
};

const ProviderFields: React.FC<ProviderFieldsProps> = ({ careTeamMemberId, isDisabled, source }) => {
    const { renderFields } = useInsuranceContactDetailsFields(isDisabled);
    return (
        <FormStack>
            <CareTeamTypeSelect value="insurance" />
            <SplitFormRow>
                <Field
                    name="metadata.insuranceProviderName"
                    component={FormikInputs.Input}
                    validate={required}
                    isRequired
                    title={intl('insuranceProviderName')}
                />
            </SplitFormRow>
            <SplitFormRow>
                <Field
                    name="insuranceCardFront"
                    component={FormikInputs.TempAndExistingImagePicker}
                    existingAvatarUrl={getCareTeamMemberInsuranceCardAvatarUrl(source, careTeamMemberId, 'front')}
                    title={intl('insuranceCardFront')}
                    imagePickerOptions={{
                        allowsEditing: true,
                        aspect: CARD_ASPECT_RATIO,
                        quality: CARD_UPLOAD_QUALITY,
                    }}
                    testID="insurance-card-front"
                />
                <Field
                    name="insuranceCardBack"
                    component={FormikInputs.TempAndExistingImagePicker}
                    existingAvatarUrl={getCareTeamMemberInsuranceCardAvatarUrl(source, careTeamMemberId, 'front')}
                    title={intl('insuranceCardBack')}
                    imagePickerOptions={{
                        allowsEditing: true,
                        aspect: CARD_ASPECT_RATIO,
                        quality: CARD_UPLOAD_QUALITY,
                    }}
                    testID="insurance-card-back"
                />
            </SplitFormRow>
            <SplitFormRow>
                <Field
                    name="metadata.primaryCarrierFirstName"
                    component={FormikInputs.Input}
                    title={intl('primaryCarrierFirstName')}
                />
                <Field
                    name="metadata.primaryCarrierLastName"
                    component={FormikInputs.Input}
                    title={intl('primaryCarrierLastName')}
                />
            </SplitFormRow>
            <SplitFormRow>
                <Field
                    name="metadata.insuranceRole"
                    component={FormikInputs.Select}
                    selectOptions={insuranceRoleSelectOptions}
                    title={intl('insuranceRole')}
                    placeholder={intl('insuranceRolePlaceholder')}
                    allowEmpty
                />
                <Field name="metadata.groupNumber" component={FormikInputs.Input} title={intl('groupNumber')} />
            </SplitFormRow>
            <SplitFormRow>
                <Field name="metadata.planNumber" component={FormikInputs.Input} title={intl('planNumber')} />
                <Field name="metadata.memberId" component={FormikInputs.Input} title={intl('memberId')} />
            </SplitFormRow>
            <SplitFormRow>
                <Field
                    name="metadata.expirationDate"
                    component={FormikInputs.DateTimePicker}
                    title={intl('expirationDate')}
                />
            </SplitFormRow>
            <SplitFormRow>
                <Field name="metadata.isPreferred" component={FormikInputs.Checkbox} title={intl('isPreferred')} />
                <Field name="metadata.isActive" component={FormikInputs.Checkbox} title={intl('isActive')} />
            </SplitFormRow>
            <CenteredFormRow>
                <Field
                    name="avatar"
                    component={FormikInputs.TempAndExistingImagePicker}
                    existingAvatarUrl={getCareTeamMemberAvatarUrl(source, careTeamMemberId)}
                    title={intl('photo')}
                    imagePickerOptions={{
                        allowsEditing: true,
                        aspect: [1, 1],
                        quality: AVATAR_UPLOAD_QUALITY,
                    }}
                />
            </CenteredFormRow>
            {renderFields()}
        </FormStack>
    );
};

export default React.memo(ProviderFields);
